import type { PeriodPreset } from "@/hooks/usePeriodFilter";

export type AdminThemePreference = "light" | "dark" | "system";
export type AdminCurrency = "BRL" | "USD";
export type AutoRefreshIntervalSeconds = 0 | 30 | 60 | 300;

export interface AdminPreferences {
  defaultPeriod: PeriodPreset;
  timezone: string;
  currency: AdminCurrency;
  autoRefreshIntervalSeconds: AutoRefreshIntervalSeconds;
  theme: AdminThemePreference;
}

export interface AdminAlertSettings {
  criticalErrorEmail: boolean;
  criticalErrorHeaderBadge: boolean;
  churnRateEmail: boolean;
  churnRateThreshold: number;
  paymentFailingEmail: boolean;
  paymentFailingThreshold: number;
  npsDropEmail: boolean;
  npsDropThreshold: number;
  newBugHeaderBadge: boolean;
  expiringTrialHeaderBadge: boolean;
}

export interface AdminThresholdSettings {
  criticalEngagementMax: number;
  criticalInactiveDays: number;
  highEngagementMax: number;
  highInactiveDays: number;
  mediumEngagementMax: number;
  mediumInactiveDays: number;
  trialHotEngagementMin: number;
  dashboardAutoRefreshMinutes: number;
}

export const ADMIN_PREFERENCES_STORAGE_KEY = "admin.settings.preferences";
export const ADMIN_ALERTS_STORAGE_KEY = "admin.settings.alerts";
export const ADMIN_THRESHOLDS_STORAGE_KEY = "admin.settings.thresholds";

const DEFAULT_PREFERENCES: AdminPreferences = {
  defaultPeriod: "30d",
  timezone: "America/Sao_Paulo",
  currency: "BRL",
  autoRefreshIntervalSeconds: 60,
  theme: "system",
};

const DEFAULT_ALERTS: AdminAlertSettings = {
  criticalErrorEmail: true,
  criticalErrorHeaderBadge: true,
  churnRateEmail: true,
  churnRateThreshold: 8,
  paymentFailingEmail: true,
  paymentFailingThreshold: 5,
  npsDropEmail: true,
  npsDropThreshold: 8,
  newBugHeaderBadge: true,
  expiringTrialHeaderBadge: true,
};

const DEFAULT_THRESHOLDS: AdminThresholdSettings = {
  criticalEngagementMax: 10,
  criticalInactiveDays: 30,
  highEngagementMax: 25,
  highInactiveDays: 14,
  mediumEngagementMax: 50,
  mediumInactiveDays: 7,
  trialHotEngagementMin: 50,
  dashboardAutoRefreshMinutes: 5,
};

function safeParse(value: string | null): unknown {
  if (!value) return null;

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toPeriod(value: unknown): PeriodPreset {
  const options: PeriodPreset[] = ["today", "yesterday", "7d", "14d", "30d", "90d", "6m", "1y", "all", "custom"];
  return typeof value === "string" && options.includes(value as PeriodPreset)
    ? (value as PeriodPreset)
    : DEFAULT_PREFERENCES.defaultPeriod;
}

function toTheme(value: unknown): AdminThemePreference {
  return value === "light" || value === "dark" || value === "system"
    ? value
    : DEFAULT_PREFERENCES.theme;
}

function toCurrency(value: unknown): AdminCurrency {
  return value === "USD" ? "USD" : "BRL";
}

function toRefresh(value: unknown): AutoRefreshIntervalSeconds {
  return value === 0 || value === 30 || value === 60 || value === 300
    ? value
    : DEFAULT_PREFERENCES.autoRefreshIntervalSeconds;
}

function toBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function toNumber(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function loadAdminPreferences(): AdminPreferences {
  const parsed = safeParse(window.localStorage.getItem(ADMIN_PREFERENCES_STORAGE_KEY));
  if (!isRecord(parsed)) return DEFAULT_PREFERENCES;

  return {
    defaultPeriod: toPeriod(parsed.defaultPeriod),
    timezone: typeof parsed.timezone === "string" && parsed.timezone.length > 0
      ? parsed.timezone
      : DEFAULT_PREFERENCES.timezone,
    currency: toCurrency(parsed.currency),
    autoRefreshIntervalSeconds: toRefresh(parsed.autoRefreshIntervalSeconds),
    theme: toTheme(parsed.theme),
  };
}

export function saveAdminPreferences(next: AdminPreferences): void {
  window.localStorage.setItem(ADMIN_PREFERENCES_STORAGE_KEY, JSON.stringify(next));
}

export function loadAdminAlerts(): AdminAlertSettings {
  const parsed = safeParse(window.localStorage.getItem(ADMIN_ALERTS_STORAGE_KEY));
  if (!isRecord(parsed)) return DEFAULT_ALERTS;

  return {
    criticalErrorEmail: toBoolean(parsed.criticalErrorEmail, DEFAULT_ALERTS.criticalErrorEmail),
    criticalErrorHeaderBadge: toBoolean(parsed.criticalErrorHeaderBadge, DEFAULT_ALERTS.criticalErrorHeaderBadge),
    churnRateEmail: toBoolean(parsed.churnRateEmail, DEFAULT_ALERTS.churnRateEmail),
    churnRateThreshold: toNumber(parsed.churnRateThreshold, DEFAULT_ALERTS.churnRateThreshold),
    paymentFailingEmail: toBoolean(parsed.paymentFailingEmail, DEFAULT_ALERTS.paymentFailingEmail),
    paymentFailingThreshold: toNumber(parsed.paymentFailingThreshold, DEFAULT_ALERTS.paymentFailingThreshold),
    npsDropEmail: toBoolean(parsed.npsDropEmail, DEFAULT_ALERTS.npsDropEmail),
    npsDropThreshold: toNumber(parsed.npsDropThreshold, DEFAULT_ALERTS.npsDropThreshold),
    newBugHeaderBadge: toBoolean(parsed.newBugHeaderBadge, DEFAULT_ALERTS.newBugHeaderBadge),
    expiringTrialHeaderBadge: toBoolean(parsed.expiringTrialHeaderBadge, DEFAULT_ALERTS.expiringTrialHeaderBadge),
  };
}

export function saveAdminAlerts(next: AdminAlertSettings): void {
  window.localStorage.setItem(ADMIN_ALERTS_STORAGE_KEY, JSON.stringify(next));
}

export function loadAdminThresholds(): AdminThresholdSettings {
  const parsed = safeParse(window.localStorage.getItem(ADMIN_THRESHOLDS_STORAGE_KEY));
  if (!isRecord(parsed)) return DEFAULT_THRESHOLDS;

  return {
    criticalEngagementMax: toNumber(parsed.criticalEngagementMax, DEFAULT_THRESHOLDS.criticalEngagementMax),
    criticalInactiveDays: toNumber(parsed.criticalInactiveDays, DEFAULT_THRESHOLDS.criticalInactiveDays),
    highEngagementMax: toNumber(parsed.highEngagementMax, DEFAULT_THRESHOLDS.highEngagementMax),
    highInactiveDays: toNumber(parsed.highInactiveDays, DEFAULT_THRESHOLDS.highInactiveDays),
    mediumEngagementMax: toNumber(parsed.mediumEngagementMax, DEFAULT_THRESHOLDS.mediumEngagementMax),
    mediumInactiveDays: toNumber(parsed.mediumInactiveDays, DEFAULT_THRESHOLDS.mediumInactiveDays),
    trialHotEngagementMin: toNumber(parsed.trialHotEngagementMin, DEFAULT_THRESHOLDS.trialHotEngagementMin),
    dashboardAutoRefreshMinutes: toNumber(parsed.dashboardAutoRefreshMinutes, DEFAULT_THRESHOLDS.dashboardAutoRefreshMinutes),
  };
}

export function saveAdminThresholds(next: AdminThresholdSettings): void {
  window.localStorage.setItem(ADMIN_THRESHOLDS_STORAGE_KEY, JSON.stringify(next));
}

export function getDefaultAdminPreferences(): AdminPreferences {
  return DEFAULT_PREFERENCES;
}

export function getDefaultAdminAlerts(): AdminAlertSettings {
  return DEFAULT_ALERTS;
}

export function getDefaultAdminThresholds(): AdminThresholdSettings {
  return DEFAULT_THRESHOLDS;
}

export function applyThemePreference(theme: AdminThemePreference): void {
  const isDark = theme === "system"
    ? window.matchMedia("(prefers-color-scheme: dark)").matches
    : theme === "dark";

  window.localStorage.setItem("admin-dark-mode", String(isDark));
  document.documentElement.classList.toggle("dark", isDark);
}
